<template>
    <div class="retell-audio-test">
        <div class="test-buttons">
            <a-button
                :type="isCalling ? 'danger' : 'primary'"
                class="test-button w-100"
                @click="toggleCall"
                :loading="isConnecting"
            >
                <i class="material-icons">{{ isCalling ? "call_end" : "phone" }}</i>
                {{ isCalling ? $t("voiceagent.endCall") : $t("voiceagent.testAudio") }}
            </a-button>

            <a-button type="default" class="variables-button" @click="toggleVariablePopover" :disabled="isCalling">
                <i class="material-icons">data_object</i>
            </a-button>
        </div>

        <a-popover
            v-model="variablePopoverVisible"
            :title="$t('voiceagent.customVariableValues')"
            trigger="click"
            placement="bottom"
            overlayClassName="variable-values-popover"
        >
            <template slot="content">
                <div v-if="extractedVariables.length === 0" class="no-variables">
                    {{ $t("voiceagent.promptNoVariablesHint") }}
                </div>
                <div v-else class="variable-values-form">
                    <div v-for="(variable, index) in extractedVariables" :key="index" class="variable-row">
                        <span class="variable-name">{{ variable.name }}:</span>
                        <a-input v-model="variable.value" :placeholder="$t('voiceagent.enterValue')" />
                    </div>
                    <div class="form-actions">
                        <a-button type="primary" @click="saveVariableValues">{{ $t("voiceagent.save") }}</a-button>
                    </div>
                </div>
            </template>
            <template slot="title">
                <div class="popover-title">{{ $t("voiceagent.customVariableValues") }}</div>
            </template>
        </a-popover>

        <div class="call-status" v-if="callStatus">
            <span :class="{ 'status-active': isCalling }">{{ callStatus }}</span>
        </div>

        <!-- Transcript içeriği -->
        <div class="transcript-container" v-if="transcript.length > 0 && isCalling">
            <h4>{{ $t("voiceagent.conversationTranscript") }}</h4>
            <div class="transcript-content">
                <div v-for="(item, index) in transcript" :key="index" class="transcript-item" :class="item.role">
                    <strong
                        >{{ item.role === "agent" ? $t("voiceagent.agent") : $t("voiceagent.user") }}:</strong
                    >
                    {{ item.content }}
                </div>
            </div>
        </div>

        <div class="test-logs" v-if="!isCalling">
            <p v-if="logs.length === 0" class="empty-logs">{{ $t("voiceagent.testResultsWillAppearHere") }}</p>
            <div v-else class="log-entries">
                <div v-for="(log, index) in logs" :key="index" class="log-entry">
                    {{ log }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { RetellWebClient } from "retell-client-js-sdk";
import log2 from "../../../../../../../utils/log2";
import { VOICE_AGENT_API } from "../../../../../../../api/voiceAgent";
const log = log2("[RetellAudioTest]");

export default {
    name: "RetellAudioTest",
    props: {
        agent: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            retellWebClient: null,
            isCalling: false,
            isConnecting: false,
            callStatus: "",
            logs: [],
            transcript: [], // Transcript için yeni state
            hasTranscriptStarted: false,
            // Değişken popover'ı için yeni state'ler
            variablePopoverVisible: false,
            extractedVariables: [],
            dynamicVariables: {},
        };
    },
    computed: {
        ...mapGetters({
            channels: "channel/channels",
        }),
        retellApiKey() {
            const retellChannel = this.channels.find((channel) => channel.type === "retell" && channel.enabled);
            return retellChannel?.payload?.apiKey || null;
        },
    },
    watch: {
        "agent.llm.general_prompt": {
            handler(newValue) {
                if (newValue) {
                    this.extractVariablesFromPrompt(newValue);
                }
            },
            immediate: true,
        },
    },
    mounted() {
        this.initializeRetellClient();
        // Prompt'tan değişkenleri çıkar
        if (this.agent?.llm?.general_prompt) {
            this.extractVariablesFromPrompt(this.agent.llm.general_prompt);
        }
    },
    beforeDestroy() {
        // Aramayı durdur ve kaynakları temizle
        if (this.retellWebClient && this.isCalling) {
            this.retellWebClient.stopCall();
        }
    },
    methods: {
        // Değişken popover'ını aç/kapat
        toggleVariablePopover() {
            this.variablePopoverVisible = !this.variablePopoverVisible;
        },

        // Prompt'tan değişkenleri çıkar
        extractVariablesFromPrompt(promptText) {
            if (!promptText) return;

            // Sistem değişkenlerinin listesi (bunları gösterme)
            const systemVariables = [
                "current_agent_state",
                "previous_agent_state",
                "current_time",
                "current_time_Europe/Istanbul",
                "call_type",
                "direction",
                "user_number",
                "agent_number",
            ];

            // {{değişken_adı}} formatındaki tüm değişkenleri bul
            const regex = /\{\{([^}]+)\}\}/g;
            const matches = [...promptText.matchAll(regex)];

            // Değişken listesini oluştur
            const variables = [];
            const variableSet = new Set(); // Tekrar eden değişkenleri önlemek için

            matches.forEach((match) => {
                const varName = match[1].trim();

                // Sistem değişkenlerini dahil etme
                if (!systemVariables.includes(varName) && !variableSet.has(varName)) {
                    variableSet.add(varName);
                    variables.push({
                        name: varName,
                        value: this.dynamicVariables[varName] || "", // Eğer daha önce bir değer girildiyse onu kullan
                    });
                }
            });

            this.extractedVariables = variables;
        },

        // Değişken değerlerini kaydet
        saveVariableValues() {
            // Değerleri dinamik değişkenler nesnesine kaydet
            this.dynamicVariables = {};
            this.extractedVariables.forEach((variable) => {
                if (variable.value) {
                    this.dynamicVariables[variable.name] = variable.value;
                }
            });

            this.addLog(
                this.$t("voiceagent.variableValuesSavedCount", { count: Object.keys(this.dynamicVariables).length })
            );
            this.variablePopoverVisible = false;
        },

        initializeRetellClient() {
            try {
                this.retellWebClient = new RetellWebClient();

                // Tüm olaylara abone ol
                this.retellWebClient.on("call_started", () => {
                    log("call_started");
                    this.addLog(this.$t("voiceagent.callStartedShort"));
                    this.callStatus = this.$t("voiceagent.callActiveStatus");
                    this.isConnecting = false;
                    this.isCalling = true;
                    this.transcript = []; // Transcript'i temizle
                    this.hasTranscriptStarted = false;
                    this.$emit("callStarted");
                });

                this.retellWebClient.on("call_ended", () => {
                    log("call_ended");
                    this.addLog(this.$t("voiceagent.callEndedShort"));
                    this.callStatus = "";
                    this.isCalling = false;
                    this.$emit("callEnded");
                });

                this.retellWebClient.on("agent_start_talking", () => {
                    log("agent_start_talking");
                    //this.addLog("Agent konuşmaya başladı");
                });

                this.retellWebClient.on("agent_stop_talking", () => {
                    log("agent_stop_talking");
                    //this.addLog("Agent konuşmayı bitirdi");
                });

                this.retellWebClient.on("audio", (audio) => {
                    // Audio verileri çok fazla log oluşturmaması için loglama yapmıyoruz
                    // log("Audio verisi alındı");
                });

                this.retellWebClient.on("update", (update) => {
                    log("update", update);
                    if (update && update.transcript) {
                        // Transcript'i güncelle
                        this.transcript = update.transcript;

                        // İlk kez transcript geldiğinde log ekle
                        if (!this.hasTranscriptStarted) {
                            this.addLog(this.$t("voiceagent.transcriptStarted"));
                            this.hasTranscriptStarted = true;
                        }
                    }
                });

                this.retellWebClient.on("metadata", (metadata) => {
                    log("metadata", metadata);
                    //this.addLog(`Metadata alındı`);
                });

                this.retellWebClient.on("error", (error) => {
                    log("error", error);
                    //this.addLog(`Hata: ${error.message || JSON.stringify(error)}`);
                    this.stopCall();
                });

                log("RetellWebClient başarıyla başlatıldı");
            } catch (error) {
                log("RetellWebClient başlatılırken hata:", error);
                this.addLog(this.$t("voiceagent.retellClientInitError", { msg: error.message }));
            }
        },
        addLog(message) {
            this.logs.unshift(`${new Date().toLocaleTimeString()}: ${message}`);
            // Maksimum 10 log tutuyoruz
            if (this.logs.length > 10) {
                this.logs.pop();
            }
        },
        async toggleCall() {
            if (this.isCalling) {
                this.stopCall();
            } else {
                await this.startCall();
            }
        },
        async startCall() {
            if (!this.agent || !this.agent.agent_id) {
                this.addLog(this.$t("voiceagent.agentIdNotFound"));
                return;
            }

            if (!this.retellWebClient) {
                this.addLog(this.$t("voiceagent.retellClientInitFailed"));
                return;
            }

            try {
                this.isConnecting = true;
                this.callStatus = this.$t("voiceagent.connectingStatus");
                this.addLog(this.$t("voiceagent.creatingCallRecord"));

                // Web çağrısı oluştur
                const callData = {
                    agent_id: this.agent.agent_id,
                    metadata: {
                        test: true,
                        siteId: this.$store.state.site.site._id,
                        userId: this.$store.state.auth.user?._id,
                        source: "voice-agent-test-tool",
                    },
                };

                // Kullanıcı tanımlı dinamik değişkenleri ekle
                if (Object.keys(this.dynamicVariables).length > 0) {
                    callData.dynamicVariables = this.dynamicVariables;
                    this.addLog(
                        this.$t("voiceagent.customVarsSending", {
                            count: Object.keys(this.dynamicVariables).length,
                        })
                    );
                }

                log("createWebCall çağrılıyor", callData);
                const response = await VOICE_AGENT_API.calls.createWeb(callData);

                if (!response || !response.access_token) {
                    throw new Error(this.$t("voiceagent.invalidResponseNoToken"));
                }

                this.addLog(this.$t("voiceagent.callRecordCreatedStarting"));

                // Çağrıyı başlat
                await this.retellWebClient.startCall({
                    accessToken: response.access_token,
                });
            } catch (error) {
                log("Çağrı başlatılırken hata:", error);
                this.addLog(
                    this.$t("voiceagent.callStartFailed", { msg: error.message || JSON.stringify(error) })
                );
                this.callStatus = "";
                this.isConnecting = false;
            }
        },
        stopCall() {
            if (this.retellWebClient && this.isCalling) {
                this.addLog(this.$t("voiceagent.callingEnding"));
                this.retellWebClient.stopCall();
            }
        },
    },
};
</script>

<style scoped lang="scss">
.retell-audio-test {
    display: flex;
    flex-direction: column;
    gap: 15px;

    .test-buttons {
        display: flex;
        gap: 10px;
    }

    .test-button,
    .variables-button {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;

        .material-icons {
            font-size: 18px;
        }
    }
    .variables-button {
        width: 50px;
    }

    .call-status {
        text-align: center;
        font-weight: 500;

        .status-active {
            color: #52c41a;
        }
    }

    .transcript-container {
        background-color: #f9f9f9;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 10px;
        margin-top: 10px;

        h4 {
            margin-top: 0;
            margin-bottom: 10px;
            font-weight: 500;
            font-size: 14px;
        }

        .transcript-content {
            //max-height: 200px;
            overflow-y: auto;

            .transcript-item {
                padding: 8px;
                margin-bottom: 8px;
                border-radius: 4px;
                font-size: 13px;

                &.agent {
                    background-color: #e6f7ff;
                }

                &.user {
                    background-color: #f6ffed;
                }

                strong {
                    font-weight: 600;
                }
            }
        }
    }

    .test-logs {
        margin-top: 10px;
        padding: 10px;
        background-color: #fff;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        min-height: 150px;
        max-height: 200px;
        overflow-y: auto;

        .empty-logs {
            color: #999;
            text-align: center;
            font-style: italic;
        }

        .log-entries {
            .log-entry {
                padding: 5px 0;
                border-bottom: 1px solid #f0f0f0;
                font-size: 12px;
                font-family: monospace;

                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }
}

.variable-values-popover {
    min-width: 350px;
    max-width: 500px;

    .popover-title {
        font-weight: 500;
    }

    .variable-values-form {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .variable-row {
            display: flex;
            align-items: center;
            gap: 10px;

            .variable-name {
                min-width: 120px;
                font-weight: 500;
            }
        }

        .form-actions {
            margin-top: 10px;
            display: flex;
            justify-content: flex-end;
        }
    }

    .no-variables {
        padding: 20px 0;
        color: #999;
        font-style: italic;
        text-align: center;
    }
}
</style>
